import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { palette, bp } from 'entity/createTheme';
import { HearingItem } from 'types/HearingSet';
import { useTranslation } from 'react-i18next';
import { useStore } from 'hooks';
import BulkDownloader from '../CacheablePlayer/BulkDownloader';
import {
  formatTime,
  getImgFromContent,
  getTotalQuestions,
  getVideosFromHearing,
} from 'utility/helpers';
import { Button, Checkbox, NoImageIcon } from 'views/components/primitive';

type Prop = {
  isSelectHearing: boolean;
  isSelected: boolean;
  hearing: HearingItem;
  toggleSelectHearingItem: (val: number) => void;
  restoreArchive: (id: number) => void;
};

const HearingItemArchive: FC<Prop> = ({
  isSelectHearing,
  hearing,
  isSelected,
  toggleSelectHearingItem,
  restoreArchive,
}) => {
  const { t } = useTranslation();
  const {
    appStore: { handleFlashMessage },
  } = useStore();

  const videos = getVideosFromHearing(hearing);

  const handleSelect = () => {
    toggleSelectHearingItem(hearing.id);
  };

  const handleViewDetail = () => {
    handleFlashMessage({ content: t('admin.common.notViewDetail'), status: 'warning' });
  };

  const handleRestore = () => {
    restoreArchive(hearing.id);
  };

  const thumbnail = getImgFromContent(hearing.question_sets[0]?.content);
  const totalQuestions = useMemo(() => getTotalQuestions(hearing), [hearing]);

  return (
    <Root isSelected={isSelected} onClick={handleViewDetail}>
      {videos.length > 0 && (
        <BulkDownloader videos={videos} top={'0px'} right={'0px'} whiteBackground={true} />
      )}
      <HearingInfo>
        <Thumbnail>
          {thumbnail ? (
            <img src={thumbnail} alt="hearing_avatar" loading="lazy" />
          ) : (
            <NoImageIcon size="100%" svgSize={28} />
          )}
        </Thumbnail>

        <div>
          <Title onClick={handleViewDetail}>{hearing.title || t('admin.hearingSet.noTitle')}</Title>
          <p>{t('hearingSet.hearingItem.numOfVideo', { count: totalQuestions })}</p>

          <UpdatedDate>
            {t('common.lastUpdated')}: {formatTime(hearing.updated_at || '', 'yyyy/MM/dd')}
          </UpdatedDate>
        </div>
      </HearingInfo>
      <ActionArea>
        {isSelectHearing ? (
          <Checkbox checked={isSelected} onChange={handleSelect} />
        ) : (
          <Button
            type="default"
            size="sm"
            label={t('admin.common.restore')}
            onClick={handleRestore}
          />
        )}
      </ActionArea>
    </Root>
  );
};

export default HearingItemArchive;

const Root = styled('div')<{ isSelected?: boolean }>(({ isSelected }) => ({
  width: 'calc(50% - 10px)',
  padding: 20,
  marginBottom: 20,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 6,
  background: palette.whitePrimary,
  border: `1px solid ${isSelected ? palette.darkMedium2 : palette.whitePrimary}`,
  cursor: 'pointer',
  boxShadow: `${palette.boxShadow} 0px 1px 9px`,
  '&:nth-of-type(odd)': {
    marginRight: 20,
  },

  [`@media ${bp.md}`]: {
    padding: 16,
    marginBottom: 16,
    width: 'calc(50% - 8px)',

    ':nth-of-type(odd)': {
      marginRight: 16,
    },
  },

  [`@media ${bp.xs}`]: {
    width: '100%',
    ':nth-of-type(odd)': {
      marginRight: 0,
    },
  },
}));

const HearingInfo = styled('div')({
  display: 'flex',
  flex: 1,
  marginBottom: 10,
  p: {
    fontSize: '0.875rem',
    lineHeight: '1.125rem',
    marginBottom: 10,
    [`@media ${bp.md}`]: {
      marginBottom: 8,
      fontSize: '16px',
    },
  },
  [`@media ${bp.md}`]: {
    marginBottom: 10,
    fontSize: '16px',
  },
});

const Thumbnail = styled('div')({
  height: 88,
  minWidth: 88,
  background: palette.blackPrimary,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: 10,
  borderRadius: 6,
  overflow: 'hidden',

  img: {
    height: '100%',
    width: 88,
    objectFit: 'contain',
    [`@media ${bp.md}`]: {
      width: 64,
    },
  },

  [`@media ${bp.md}`]: {
    height: 64,
    minWidth: 64,
  },
});

const Title = styled('p')({
  fontWeight: 'bold',

  '@supports not (overflow-wrap: anywhere)': {
    wordBreak: 'break-word',
  },
  '@supports (overflow-wrap: anywhere)': {
    overflowWrap: 'anywhere',
  },
});

const UpdatedDate = styled('p')({
  color: palette.deepBlueLight,
  fontSize: '0.875rem !important',
});

const ActionArea = styled('div')({
  display: 'flex',
  marginTop: 10,
  justifyContent: 'flex-end',
  alignItems: 'flex-end',

  [`@media ${bp.md}`]: {
    marginTop: 0,
  },
});
